import { Button } from "@dash/form";
import { Page, PageHeader } from "@dash/page";
import { QuerySkeleton, Skeleton } from "@dash/skeleton";
import { Theme, useTheme } from "@emotion/react";
import React, { useContext, useState } from "react";
import { MdAdd, MdInsights } from "react-icons/md";
import { Link } from "react-router-dom-v5-compat";

import { IamAuthContext } from "@megaron/auth-react";
import { Report } from "@megaron/crm-contracts";
import { getMegaraxPath } from "@megaron/megarax-uri";
import { useClientManager } from "@megaron/react-clients";

import { AddReportDialog } from "./AddReportDialog";

export const ReportsPage: React.FC = () => {
  const reportsQuery = useClientManager("crm").reportsQuery().useQuery({});
  const canEdit = useContext(IamAuthContext).iamUser?.roles.includes("crm.admin") ?? false;
  const [isAdding, setIsAdding] = useState(false);

  return (
    <Page css={{ maxWidth: "1680px", paddingBottom: "2rem" }}>
      <PageHeader
        actions={
          canEdit && (
            <Button icon={<MdAdd />} onClick={() => setIsAdding(true)}>
              Nowy raport
            </Button>
          )
        }
      >
        Raporty
      </PageHeader>

      <QuerySkeleton query={reportsQuery} customSekelton={<ReportCardsSkeleton />}>
        {(reports) => (
          <ReportGrid>
            {reports.map((report) => (
              <ReportCard key={report.uuid} report={report} />
            ))}
          </ReportGrid>
        )}
      </QuerySkeleton>

      {isAdding && <AddReportDialog onClose={() => setIsAdding(false)} />}
    </Page>
  );
};

const ReportGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div css={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "0.75rem" }}>
    {children}
  </div>
);

const ReportCardsSkeleton: React.FC = () => {
  const theme = useTheme();

  return (
    <ReportGrid>
      {[0, 1, 2].map((i) => (
        <div key={i} css={cardCss(theme)}>
          <ReportCardFrame icon={<Skeleton width="100%" height="100%" />}>
            <TextLine height={titleLineHeight} width="55%" />
            <span css={{ display: "flex", flexDirection: "column" }}>
              <TextLine height={descriptionLineHeight} width="95%" />
              <TextLine height={descriptionLineHeight} width="70%" />
            </span>
          </ReportCardFrame>
        </div>
      ))}
    </ReportGrid>
  );
};

const titleLineHeight = "1.375rem";
const descriptionLineHeight = "1.125rem";

const TextLine: React.FC<{ height: string; width: string }> = ({ height, width }) => (
  <span css={{ height, display: "flex", alignItems: "center" }}>
    <Skeleton width={width} height="60%" css={{ borderRadius: "4px" }} />
  </span>
);

const ReportCardFrame: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => {
  const theme = useTheme();

  return (
    <>
      <span
        css={{
          flexShrink: 0,
          width: "40px",
          height: "40px",
          overflow: "hidden",
          borderRadius: theme.smallBorderRadius,
          background: theme.colors.primaryLight,
          color: theme.colors.primary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1.375rem",
        }}
      >
        {icon}
      </span>
      <span css={{ display: "flex", flexDirection: "column", gap: "0.25rem", minWidth: 0, flex: 1 }}>{children}</span>
    </>
  );
};

const cardCss = (theme: Theme) =>
  ({
    display: "flex",
    gap: "0.75rem",
    padding: "1rem",
    background: "white",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    borderRadius: theme.mediumBorderRadius,
  }) as const;

const ReportCard: React.FC<{ report: Report }> = ({ report }) => {
  const theme = useTheme();

  return (
    <Link
      to={getMegaraxPath.uri("megarax://crm/reports/id/{id}", { id: report.uuid })}
      css={{
        ...cardCss(theme),
        transition: "border-color 120ms, box-shadow 120ms",
        "&:hover": {
          textDecoration: "none",
          borderColor: theme.colors.primary,
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
        },
      }}
    >
      <ReportCardFrame icon={<MdInsights />}>
        <span css={{ fontWeight: 600, fontSize: "1rem", lineHeight: titleLineHeight, color: "#1d1c1a" }}>
          {report.title}
        </span>
        <span
          className="clamped-text"
          css={{
            fontSize: "0.8125rem",
            lineHeight: descriptionLineHeight,
            minHeight: `calc(2 * ${descriptionLineHeight})`,
            color: theme.colors.secondaryText,
            WebkitLineClamp: 2,
          }}
        >
          {report.description}
        </span>
      </ReportCardFrame>
    </Link>
  );
};
